import { Link } from "react-router-dom";

import { deriveHighestVerificationTier } from "../../lib/dashboard";
import type { DashboardSnapshot } from "../../lib/dashboard";
import { useReadiness } from "../../hooks/useReadiness";
import { cn } from "../../lib/utils";
import { TierBadge } from "../ui/tier-badge";
import { computeReadinessChipDots } from "./ReadinessChip.helpers";

/**
 * Plan 435 M2 (mfh-hero-readiness-chip). Row 1 of `HomeHero`: tier badge,
 * score, label, and a four-dot money-category summary — present in EVERY
 * hero state including the gate's `loading`/`off` renders (tech-spec
 * Components row), because it reads `useReadiness()` directly and is
 * entirely independent of the marketplace gate/reads. Always links to the
 * Passport.
 */
export function ReadinessChip({
  snapshot,
  className,
  onClick,
}: {
  snapshot: Pick<DashboardSnapshot, "financialInputs">;
  className?: string;
  /** Plan 435 M2 (mfh-hero-analytics carryover) — `HomeHero` wires this to
   *  fire `home_hero_cta_clicked{cta:"chip"}` before the Link navigates. */
  onClick?: () => void;
}) {
  const { data } = useReadiness();
  const tier = deriveHighestVerificationTier(snapshot);
  const score = data?.result?.score ?? null;
  const label = data?.result?.label ?? "Not yet rated";
  const dots = computeReadinessChipDots(snapshot.financialInputs);

  return (
    <Link
      className={cn(
        "inline-flex max-w-full items-center gap-2 rounded-full border border-panel-ink-foreground/15 bg-panel-ink-foreground/10 px-3 py-1.5 text-panel-ink-foreground transition hover:bg-panel-ink-foreground/15",
        className,
      )}
      data-testid="home-readiness-chip"
      onClick={onClick}
      to="/dashboard?view=passport"
    >
      <TierBadge
        className="shrink-0 whitespace-nowrap"
        size="sm"
        tier={tier}
        variant="on-ink"
      />
      <span
        className="shrink-0 whitespace-nowrap text-sm font-semibold"
        data-testid="home-readiness-chip-score"
      >
        {typeof score === "number" ? score : "—"}
      </span>
      <span
        className="shrink truncate whitespace-nowrap text-xs text-panel-ink-foreground/70"
        data-testid="home-readiness-chip-label"
      >
        {label}
      </span>
      <span
        aria-hidden="true"
        className="flex shrink-0 items-center gap-1"
        data-testid="home-readiness-chip-dots"
      >
        {dots.map((on, index) => (
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              on ? "bg-success" : "bg-panel-ink-foreground/25",
            )}
            data-testid={`home-readiness-chip-dot-${index}`}
            key={index}
          />
        ))}
      </span>
    </Link>
  );
}
